import { Box, Flex, Link } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import type { ContactCardData, ContactListBlockData } from '../../../types';

interface ContactListBlockProps {
  data: ContactListBlockData;
}

// Initials avatar for a created contact.
const initials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('');

const ContactCard = ({ contact }: { contact: ContactCardData }) => (
  <Box
    border="1px solid"
    borderColor="secondary-200"
    borderRadius="10px"
    bg="base-white"
    px="14px"
    py="12px"
    _hover={{
      borderColor: 'primary-300',
      boxShadow: '0 1px 3px rgba(16,24,40,0.06)',
    }}
    transition="all 0.12s"
  >
    <Flex align="center" gap="10px">
      <Flex
        align="center"
        justify="center"
        w="34px"
        h="34px"
        borderRadius="full"
        bg="primary-50"
        flexShrink={0}
      >
        <Text variant="small" className="font-semibold text-primary-600">
          {initials(contact.name) || '?'}
        </Text>
      </Flex>
      <Box flex="1" minW={0}>
        <Text variant="span" className="text-secondary-900 font-semibold">
          {contact.name}
        </Text>
        {contact.email && (
          <Text
            variant="small"
            className="text-secondary-500"
            style={{ marginTop: 1 }}
          >
            {contact.email}
          </Text>
        )}
      </Box>
      {contact.url && (
        <Link
          href={contact.url}
          isExternal
          color="primary-500"
          onClick={(e) => e.stopPropagation()}
          flexShrink={0}
        >
          <Text variant="small" className="font-medium text-primary-500">
            View contact ↗
          </Text>
        </Link>
      )}
    </Flex>
  </Box>
);

// A list of contacts that were just created from uploaded resumes — one card
// each, with a link to the new candidate record.
export const ContactListBlock = ({ data }: ContactListBlockProps) => {
  if (!data.contacts?.length) return null;
  return (
    <Flex direction="column" gap="8px" maxW="520px">
      {data.summary && (
        <Text variant="small" className="text-secondary-500">
          {data.summary}
        </Text>
      )}
      {data.contacts.map((contact) => (
        <ContactCard key={contact.id} contact={contact} />
      ))}
    </Flex>
  );
};
